/* eslint-disable react/prop-types */
import type { ChangeEvent } from 'react';
import { memo, useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import { IcDeleteTrash, IcPlus } from '@/assets/images';
import { TimePicker } from '@/components';
import {
  DATE_TIME_FORMAT_WITH_SECONDS,
  TIME_FORMAT_WITH_SECONDS,
} from '@/config';
import {
  CFSEvent,
  IAdditionalInfo,
  IUnitShift,
  PINNED_LIVE_INFO_EVENT,
} from '@/models';

import { TimelineLogItem } from '../utils/timelineUtils';

interface VerticalStepperProps {
  logs: TimelineLogItem[];
  historyUnitShifts: { [key: string]: IUnitShift };
  selectedEvents?: string[];
  cfsAdditionalInfo?: IAdditionalInfo[];
  isEditing?: boolean;
  draggableNaIds?: Set<string>;
  localNaGroupOverrides?: Record<string, number>;
  onTimeChange?: (logId: string, value: number | null) => void;
  onClearTime?: (logId: string) => void;
  onDescriptionChange?: (logId: string, value: string) => void;
  getDescriptionDraftValue?: (logId: string, fallback: string) => string;
  onAddCustomEvent?: () => void;
  onDeleteCustomEvent?: (logId: string) => void;
  onReorder?: (activeId: string, overId: string) => void;
  currentUserId?: string;
}

const TimeLineHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
}));

const TimelineDot = styled(Box)(({ theme }) => ({
  width: 1,
  backgroundColor: theme.palette.primary.main,
  position: 'relative',
  alignSelf: 'stretch',
  '::before': {
    position: 'absolute',
    content: '""',
    width: 7,
    height: 7,
    left: -3,
    top: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const CustomEventDescriptionInput = memo(
  ({
    logId,
    value,
    onChange,
  }: {
    logId: string;
    value: string;
    onChange?: (logId: string, value: string) => void;
  }) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
      setLocalValue(value);
    }, [logId]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setLocalValue(nextValue);
      onChange?.(logId, nextValue);
    };

    return (
      <TextField
        size="small"
        sx={{ width: 360 }}
        placeholder="Event description"
        value={localValue}
        onChange={handleChange}
      />
    );
  },
);

CustomEventDescriptionInput.displayName = 'CustomEventDescriptionInput';

const TimelineTimeInput = memo(
  ({
    logId,
    value,
    onCommit,
    onClear,
  }: {
    logId: string;
    value: moment.Moment | null;
    onCommit?: (logId: string, value: number | null) => void;
    onClear?: (logId: string) => void;
  }) => {
    const [localValue, setLocalValue] = useState<moment.Moment | null>(value);
    const externalValue = value?.valueOf() ?? null;

    useEffect(() => {
      setLocalValue(value ? value.clone() : null);
    }, [logId, externalValue]);

    const commitValue = (nextValue: moment.Moment | null) => {
      if (!nextValue) {
        onClear?.(logId);
        return;
      }

      if (!nextValue.isValid()) {
        return;
      }

      onCommit?.(logId, nextValue.clone().millisecond(0).valueOf());
    };

    return (
      <TimePicker
        value={localValue}
        onChange={(nextValue) => {
          setLocalValue(nextValue);
        }}
        onAccept={(nextValue) => {
          commitValue(nextValue);
        }}
        ampm={false}
        views={['hours', 'minutes', 'seconds']}
        format={TIME_FORMAT_WITH_SECONDS}
        closeOnSelect={false}
        slotProps={{
          field: {
            clearable: true,
            onClear: () => {
              setLocalValue(null);
              onClear?.(logId);
            },
          },
          textField: {
            size: 'small',
            sx: { width: 220 },
            inputProps: { style: { textAlign: 'right', fontSize: 14 } },
            onBlur: () => {
              commitValue(localValue);
            },
          },
        }}
      />
    );
  },
);

TimelineTimeInput.displayName = 'TimelineTimeInput';

export const CFSTimelineStepper = ({
  logs,
  historyUnitShifts,
  cfsAdditionalInfo = [],
  isEditing = false,
  draggableNaIds = new Set(),
  onTimeChange,
  onClearTime,
  onDescriptionChange,
  getDescriptionDraftValue,
  onAddCustomEvent,
  onDeleteCustomEvent,
  onReorder,
  currentUserId,
  localNaGroupOverrides = {},
}: VerticalStepperProps) => {
  const t = useTheme();
  type TimelineAdditionalInfo = IAdditionalInfo & {
    _id: string;
    event: typeof PINNED_LIVE_INFO_EVENT;
  };

  type TimelineItem = TimelineLogItem | TimelineAdditionalInfo;

  const isTimelineAdditionalInfo = (
    item: TimelineItem,
  ): item is TimelineAdditionalInfo => {
    return 'description' in item && item.event === PINNED_LIVE_INFO_EVENT;
  };

  const groupLogsByDate = (): { [k: string]: TimelineLogItem[] } => {
    const map: { [k: string]: TimelineLogItem[] } = {};
    for (const log of logs) {
      const logId = log._id ? String(log._id) : '';
      const overrideDay =
        log.overriddenAt === null && logId
          ? localNaGroupOverrides[logId] ?? log.groupDay ?? undefined
          : undefined;
      const effectiveTimestamp =
        typeof overrideDay === 'number'
          ? overrideDay
          : typeof log.overriddenAt === 'number'
          ? log.overriddenAt
          : new Date(log.createdAt).getTime();
      const dayKey = moment(effectiveTimestamp).startOf('day').valueOf();
      if (map[dayKey]) {
        map[dayKey].push(log);
      } else {
        map[dayKey] = [log];
      }
    }
    return map;
  };

  const groupAdditionalInfoByDate = (): {
    [k: string]: TimelineAdditionalInfo[];
  } => {
    const map: { [k: string]: TimelineAdditionalInfo[] } = {};
    for (const additionalInfo of cfsAdditionalInfo) {
      const createdAt =
        moment(additionalInfo.createdAt).startOf('day').unix() * 1000;
      const timelineAdditionalInfo: TimelineAdditionalInfo = {
        ...additionalInfo,
        _id: `pinned-live-info:${additionalInfo.createdAt}:${
          additionalInfo.createdBy?._id ?? 'unknown'
        }:${additionalInfo.description}`,
        event: PINNED_LIVE_INFO_EVENT,
      };
      if (map[createdAt]) {
        map[createdAt].push(timelineAdditionalInfo);
      } else {
        map[createdAt] = [timelineAdditionalInfo];
      }
    }
    Object.keys(map).forEach((key) => {
      map[key].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    });
    return map;
  };

  const groupedLogs = groupLogsByDate();
  const groupedAdditionalInfo = groupAdditionalInfoByDate();

  const formatDate = (timestamp: string) => {
    return moment(Number(timestamp)).format('MMM DD YYYY');
  };

  const getLogData = (log: TimelineItem): { label: string; value?: string } => {
    if (isTimelineAdditionalInfo(log)) {
      return {
        label: 'Pinned Live Info',
        value: log.description.replace('*', ''),
      };
    }

    const unitShift = log.data?.new?.assignedUnitShifts?.unitShift;
    const matchingUnitShift = unitShift
      ? historyUnitShifts?.[unitShift]
      : undefined;

    switch (log.event) {
      case CFSEvent.CALL_RECEIVED:
        return {
          label: 'Call Received',
        };
      case CFSEvent.CALL_ANSWERED:
        return {
          label: 'Call Answered by Dispatcher',
        };
      case CFSEvent.CREATED:
        return {
          label: 'CFS Created',
        };
      case CFSEvent.UNIT_SHIFT_ASSIGNED:
        return {
          label: 'Assigned',
          value: matchingUnitShift ? matchingUnitShift.unit.name : 'n/a',
        };
      case CFSEvent.UNIT_SHIFT_IS_EN_ROUTE:
        return {
          label: 'Enroute',
          value: matchingUnitShift ? matchingUnitShift.unit.name : 'n/a',
        };
      case CFSEvent.UNIT_SHIFT_IS_IN_THE_AREA:
        return {
          label: 'In The Area',
          value: matchingUnitShift ? matchingUnitShift.unit.name : 'n/a',
        };
      case CFSEvent.UNIT_SHIFT_IS_ON_SCENE:
        return {
          label: 'On scene',
          value: matchingUnitShift ? matchingUnitShift.unit.name : 'n/a',
        };
      case CFSEvent.UNIT_SHIFT_COMPLETED:
        return {
          label: 'Completed',
          value: matchingUnitShift ? matchingUnitShift.unit.name : 'n/a',
        };
      case CFSEvent.UNIT_SHIFT_CANCELED:
        return {
          label: 'Completed(Canceled)',
          value: matchingUnitShift ? matchingUnitShift.unit.name : 'n/a',
        };
      case CFSEvent.MARK_FIRE_UNDER_CONTROL:
        return {
          label: 'Fire under control',
        };
      case CFSEvent.CLOSED:
        return {
          label: 'CFS Closed',
        };
      case CFSEvent.UPDATED_INCIDENT_CODES:
        return {
          label: 'CFS dispatched',
        };
      case CFSEvent.CUSTOM_EVENT:
        return {
          label: log.data?.new?.description || 'Custom event',
        };
      default:
        return {
          label: 'Unknown',
        };
    }
  };

  const formatDateTime = (date: Date | string | number) => {
    return moment(date).format(TIME_FORMAT_WITH_SECONDS);
  };

  const getDisplayTime = (log: TimelineLogItem) => {
    if (log.overriddenAt === null) return 'N/A';
    if (typeof log.overriddenAt === 'number') {
      return formatDateTime(log.overriddenAt);
    }
    return formatDateTime(log.createdAt);
  };

  const getTimePickerValue = (log: TimelineLogItem) => {
    if (log.overriddenAt === null) return null;
    if (typeof log.overriddenAt === 'number') {
      return moment(log.overriddenAt);
    }
    return moment(log.createdAt);
  };

  const getEditedTooltip = (log: TimelineLogItem) => {
    if (!log.editedAt) return null;
    const editedByName = log.editedBy
      ? `By ${log.editedBy.fullName || log.editedBy.email}`
      : 'Unknown';
    const editedAt = moment(log.editedAt).format(DATE_TIME_FORMAT_WITH_SECONDS);
    return `${editedByName} • ${editedAt}`;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onReorder || !event.over) return;
    onReorder(String(event.active.id), String(event.over.id));
  };

  const renderLogRowContent = (log: TimelineLogItem) => {
    const logId = log._id ? String(log._id) : '';
    const logData = getLogData(log);
    const editedTooltip = getEditedTooltip(log);
    const canDeleteCustom =
      isEditing &&
      log.event === CFSEvent.CUSTOM_EVENT &&
      Boolean(onDeleteCustomEvent) &&
      log.createdBy?._id === currentUserId;

    return (
      <>
        <Box display="flex" alignItems="center" width={isEditing ? 160 : 55}>
          {isEditing ? (
            <TimelineTimeInput
              logId={logId}
              value={getTimePickerValue(log)}
              onCommit={(editedLogId, value) => {
                if (value === null) {
                  onClearTime?.(editedLogId);
                  return;
                }

                const base =
                  typeof log.overriddenAt === 'number'
                    ? moment(log.overriddenAt)
                    : moment(log.createdAt);
                const adjusted = base
                  .clone()
                  .hour(moment(value).hour())
                  .minute(moment(value).minute())
                  .second(moment(value).second())
                  .millisecond(0);
                onTimeChange?.(editedLogId, adjusted.valueOf());
              }}
              onClear={onClearTime}
            />
          ) : (
            <Typography padding={0.5} fontSize={14} fontWeight={400}>
              {getDisplayTime(log)}
            </Typography>
          )}
        </Box>
        <TimelineDot></TimelineDot>
        <Box display="flex" alignItems="center" gap={1}>
          {isEditing && log.event === CFSEvent.CUSTOM_EVENT && log.isNew ? (
            <CustomEventDescriptionInput
              logId={logId}
              value={
                getDescriptionDraftValue
                  ? getDescriptionDraftValue(
                      logId,
                      log.data?.new?.description || '',
                    )
                  : log.data?.new?.description || ''
              }
              onChange={onDescriptionChange}
            />
          ) : (
            <>
              <Typography padding={0.5} fontWeight={700} fontSize={14}>
                {logData.label}
              </Typography>
              {logData.value && (
                <>
                  <Typography padding={0.5} color={t.palette.text.secondary}>
                    •
                  </Typography>
                  <Typography
                    color={t.palette.text.secondary}
                    padding={0.5}
                    fontWeight={400}
                    fontSize={14}
                  >
                    {logData.value}
                  </Typography>
                </>
              )}
            </>
          )}
          {editedTooltip && (
            <Tooltip title={editedTooltip}>
              <Chip label="Edited" size="small" />
            </Tooltip>
          )}
        </Box>
        {canDeleteCustom && (
          <IconButton
            size="small"
            onClick={() => onDeleteCustomEvent?.(logId)}
            aria-label="Delete custom event"
          >
            <IcDeleteTrash fontSize="small" color="red" />
          </IconButton>
        )}
      </>
    );
  };

  const StaticTimelineLogRow = ({ log }: { log: TimelineLogItem }) => {
    return (
      <Stack direction="row" gap={2} sx={{ pl: 3 }}>
        {renderLogRowContent(log)}
      </Stack>
    );
  };

  const ViewModeSortableRow = ({
    log,
    isDraggable,
  }: {
    log: TimelineLogItem;
    isDraggable: boolean;
  }) => {
    const logId = log._id ? String(log._id) : '';
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: logId,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
    };

    return (
      <Stack
        ref={setNodeRef}
        style={style}
        direction="row"
        gap={2}
        sx={{ pl: 3 }}
      >
        {renderLogRowContent(log)}
        {isDraggable && (
          <IconButton
            size="small"
            sx={{ cursor: 'grab' }}
            {...attributes}
            {...listeners}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
        )}
      </Stack>
    );
  };

  const TimelineAdditionalInfoRow = ({
    info,
  }: {
    info: TimelineAdditionalInfo;
  }) => {
    const logData = getLogData(info);
    return (
      <Stack direction="row" gap={2} sx={{ pl: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          width={55}
          justifyContent="flex-end"
        >
          <Typography padding={0.5} fontSize={14} fontWeight={400}>
            {formatDateTime(info.createdAt)}
          </Typography>
        </Box>
        <TimelineDot></TimelineDot>
        <Box display="flex" alignItems="center" flex={1}>
          <Typography padding={0.5} fontWeight={700} fontSize={14}>
            {logData.label}
          </Typography>
          {logData.value && (
            <>
              <Typography padding={0.5} color={t.palette.text.secondary}>
                •
              </Typography>
              <Typography
                color={t.palette.text.secondary}
                padding={0.5}
                fontWeight={400}
                fontSize={14}
              >
                {logData.value}
              </Typography>
            </>
          )}
        </Box>
      </Stack>
    );
  };

  const draggableIds = isEditing
    ? []
    : logs
        .filter((log: TimelineLogItem) => {
          const logId = log._id ? String(log._id) : '';
          return log.overriddenAt === null && logId;
        })
        .map((log) => String(log._id));

  const sortableIds = isEditing
    ? draggableIds
    : logs
        .map((log) => (log._id ? String(log._id) : null))
        .filter((id): id is string => Boolean(id));

  return (
    <div style={{ overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          <Stack>
            {[
              ...new Set([
                ...Object.keys(groupedLogs),
                ...Object.keys(groupedAdditionalInfo),
              ]),
            ]
              .sort()
              .map((k) => {
                const additionalInfo = groupedAdditionalInfo[k] || [];
                return (
                  <Stack key={k}>
                    <TimeLineHeader>
                      <Typography fontSize={14} fontWeight={700} px={2} py={2}>
                        {formatDate(k)}
                      </Typography>
                    </TimeLineHeader>

                    {groupedLogs[k]?.map((log) => {
                      const logId = log._id ? String(log._id) : '';
                      const isDraggable =
                        log.overriddenAt === null &&
                        logId &&
                        (isEditing ? draggableNaIds.has(logId) : true);

                      if (!isEditing) {
                        return (
                          <ViewModeSortableRow
                            key={String(log._id)}
                            log={log}
                            isDraggable={!!isDraggable}
                          />
                        );
                      }

                      return (
                        <StaticTimelineLogRow key={String(log._id)} log={log} />
                      );
                    })}
                    {additionalInfo.map((info) => (
                      <TimelineAdditionalInfoRow key={info._id} info={info} />
                    ))}
                  </Stack>
                );
              })}
          </Stack>
        </SortableContext>
      </DndContext>
      {isEditing && onAddCustomEvent && (
        <Box sx={{ pl: 3, py: 2 }}>
          <Button
            variant="contained"
            color="inherit"
            onClick={onAddCustomEvent}
          >
            <IcPlus />
            Add new
          </Button>
        </Box>
      )}
    </div>
  );
};
